// components/ErrorBoundary/DefaultErrorUI.tsx — Default fallback error UI
// SENGAJA tanpa @/components/ui + lucide-react: file ini ikut entry publik
// (PublicApp → ErrorBoundary) — import ui di sini menyeret chunk ui 194KB +
// radix/lucide ke SEMUA halaman publik. Pakai elemen biasa + SVG inline.
"use client";

import React from "react";
import { createErrorInfo, getUserFriendlyMessage } from "@/utilities/errorHandler";

interface DefaultErrorUIProps {
  error: Error;
  showRetryButton: boolean;
  onReset: () => void;
  onGoHome: () => void;
}

const DefaultErrorUI: React.FC<DefaultErrorUIProps> = ({ error, showRetryButton, onReset, onGoHome }) => {
  const errorInfo = createErrorInfo(error);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-900">
      <div className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            {/* Error Icon */}
            <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/20 rounded-full">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
            </div>

            {/* Error Title */}
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              Maaf, terjadi kesalahan
            </h2>

            {/* Error Message */}
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 max-w-md">
              {getUserFriendlyMessage(
                error,
                "Aplikasi mengalami kesalahan yang tidak diharapkan."
              )}
            </p>

            {/* Error Details (optional, can be expanded) */}
            <details className="w-full mb-4 text-left">
              <summary className="text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
                Detail Error
              </summary>
              <div className="mt-2 p-3 bg-slate-100 dark:bg-slate-800 rounded text-xs font-mono text-slate-700 dark:text-slate-300 max-h-32 overflow-auto">
                <p>Error: {error.message}</p>
                {error.stack && (
                  <pre className="mt-2 whitespace-pre-wrap">{error.stack}</pre>
                )}
              </div>
            </details>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-center">
              {showRetryButton && (
                <button
                  type="button"
                  onClick={onReset}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                  Coba Lagi
                </button>
              )}

              <button
                type="button"
                onClick={onGoHome}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" /></svg>
                Kembali ke Beranda
              </button>
            </div>

            {/* Support Link (optional) */}
            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
              Jika masalah berlanjut, silakan hubungi dukungan teknis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultErrorUI;
